import { Exact } from 'common/repositories';
import { PaginationMeta, Sort } from 'common/repositories/common.model';
import { useRouter } from 'next/router';
// import { IColumn } from 'components/elements/table-component/table-component';
import React, { useCallback, useMemo, useState } from 'react';

function useApplyQuerySort<
  QueryData,
  QueryInput extends Exact<{ params: Exact<{ sort?: string | null }> }>,
>(
  getSorterMeta: (data: QueryData) => Sort,
  columns: any[],
  meta?: PaginationMeta,
) {
  const { query } = useRouter();
  const [sort, setSort] = useState<Sort>();

  const currentSortValue = useMemo(() => {
    return sort?.value || sort?.default;
  }, [sort?.default, sort?.value]);

  const key = useMemo(() => {
    return currentSortValue?.replace(/^-(.*)$/g, '$1');
  }, [currentSortValue]);

  const onColumnClick = useCallback(
    (column: any) => {
      if (sort && column?.sortName && sort.options.includes(column?.sortName)) {
        const modifier =
          key === column?.sortName && !currentSortValue?.startsWith('-', 0)
            ? '-'
            : '';
        setSort({
          ...sort,
          value: modifier + column?.sortName,
        });
      }
    },
    [currentSortValue, key, sort],
  );

  const sortedColumns = useMemo<any>(
    () =>
      columns &&
      columns.map((column) => ({
        ...column,
        getToggleSortingHandler: onColumnClick,
        isSorted: column?.sortName === key,
        isSortedDesc: currentSortValue?.startsWith('-', 0),
      })),
    [columns, onColumnClick, key, currentSortValue],
  );

  const values = React.useMemo(() => {
    return {
      input: {
        params: {
          sort: sort?.value || query?.sort || undefined,
        },
      } as QueryInput,
      onSuccess(data: QueryData) {
        setSort(getSorterMeta(data));
      },
      extra: {
        columns: sortedColumns,
        meta,
      },
    };
  }, [getSorterMeta, meta, query?.sort, sort?.value, sortedColumns]);

  return values;
}

export default useApplyQuerySort;
